"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/** 1 … 4 5 [6] 7 8 … 20 - always at most 7 slots. */
function pageWindow(current: number, count: number): (number | "gap")[] {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const out: (number | "gap")[] = [1];
  const from = Math.max(2, current - 1);
  const to = Math.min(count - 1, current + 1);
  if (from > 2) out.push("gap");
  for (let i = from; i <= to; i++) out.push(i);
  if (to < count - 1) out.push("gap");
  out.push(count);
  return out;
}

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/** "Rows per page" picker (10 / 20 / 50 / 100), shown in a table's toolbar. */
export function PageSizeSelect({ value, onChange }: { value: number; onChange: (size: number) => void }) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-ink-muted">
      Rows per page
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label="Rows per page"
        className="h-8 rounded-lg border border-line bg-white px-2 text-xs font-semibold text-ink-soft"
      >
        {PAGE_SIZE_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Numbered page bar, always visible: "Showing 1–10 of 42" + previous / 1 2 3 … / next. */
export function Pagination({
  page,
  pageSize,
  total,
  shown,
  onPage,
  className,
}: {
  page: number; // 1-based
  pageSize: number;
  total: number;
  /** how many rows are on the current page */
  shown: number;
  onPage: (page: number) => void;
  className?: string;
}) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), pageCount);
  const first = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const last = Math.min(total, (current - 1) * pageSize + shown);
  const goto = (p: number) => onPage(Math.min(pageCount, Math.max(1, p)));

  return (
    <div className={cn("flex flex-col items-center justify-between gap-3 sm:flex-row", className)}>
      <p className="text-xs text-ink-muted">{total === 0 ? "No records" : `Showing ${first}–${last} of ${total}`}</p>
      <nav className="flex items-center gap-1.5" aria-label="Pagination">
        <button
          type="button"
          onClick={() => goto(current - 1)}
          disabled={current <= 1}
          aria-label="Previous page"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line text-ink-soft transition-colors hover:bg-cream disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {pageWindow(current, pageCount).map((p, i) =>
          p === "gap" ? (
            <span key={`gap-${i}`} className="px-1 text-xs text-ink-muted">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => goto(p)}
              aria-label={`Page ${p}`}
              aria-current={p === current ? "page" : undefined}
              className={cn(
                "inline-flex h-8 min-w-8 items-center justify-center rounded-lg border px-2 text-xs font-semibold tabular-nums transition-colors",
                p === current ? "border-brand-600 bg-brand-600 text-white" : "border-line text-ink-soft hover:bg-cream",
              )}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => goto(current + 1)}
          disabled={current >= pageCount}
          aria-label="Next page"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line text-ink-soft transition-colors hover:bg-cream disabled:opacity-40"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </nav>
    </div>
  );
}
